import React, { useEffect, useState, useCallback } from "react";
import { Loader2, CalendarCheck, Save, UserX, CheckCircle2, CalendarPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PendingAttendance, TeacherHolidays } from "@/pages/star/PendingAttendance";

export default function StudentAttendance() {
  const [classes, setClasses] = useState([]);
  const [date, setDate] = useState("");
  const [klass, setKlass] = useState("");
  const [roster, setRoster] = useState([]);
  const [totals, setTotals] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [leaveFor, setLeaveFor] = useState(null); // student obj for the leave dialog
  const [lFrom, setLFrom] = useState("");
  const [lTo, setLTo] = useState("");
  const [lReason, setLReason] = useState("");
  const [lSaving, setLSaving] = useState(false);
  const [pendKey, setPendKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/teacher/student-attendance/classes");
        setClasses(data.classes); setDate(data.today);
      } catch { toast.error("Could not load your classes"); }
    })();
  }, []);

  const loadRoster = useCallback(async () => {
    if (!date || !klass) return;
    setLoading(true); setTotals(null);
    try {
      const { data } = await api.get("/teacher/student-attendance/roster", { params: { date, klass } });
      setRoster(data.roster); setSavedAt(data.saved_at); setTotals(data.totals);
    } catch { toast.error("Could not load roster"); }
    finally { setLoading(false); }
  }, [date, klass]);

  useEffect(() => { loadRoster(); }, [loadRoster]);

  const setStudent = (id, patch) => setRoster((r) => r.map((s) => (s.student_id === id ? { ...s, ...patch } : s)));
  const toggleAbsent = (s) => {
    if (s.status === "absent") setStudent(s.student_id, { status: "present", absence_type: null });
    else setStudent(s.student_id, { status: "absent", absence_type: s.advance_leave ? "informed" : "uninformed" });
  };

  const save = async () => {
    setSaving(true);
    try {
      const entries = roster.map((s) => ({ student_id: s.student_id, status: s.status, absence_type: s.absence_type }));
      const { data } = await api.post("/teacher/student-attendance/save", { date, klass, entries });
      setTotals(data.totals); setSavedAt(data.saved_at); setPendKey((k) => k + 1);
      const pe = data.parent_emails;
      if (pe && pe.logged > 0) {
        toast.success(`Attendance saved. ${pe.logged} parent absence email${pe.logged > 1 ? "s" : ""} ${pe.live ? "sent" : "logged (TEST mode — not sent)"}.`);
      } else {
        toast.success("Attendance saved");
      }
      const fu = (data.followups || []).filter((f) => f.needs_call || f.needs_meeting);
      fu.forEach((f) => {
        if (f.needs_meeting) toast.warning(`${f.name}: ${f.streak} days absent — arrange a parent meeting. See Follow-ups tab.`, { duration: 7000 });
        else toast.warning(`${f.name}: ${f.streak} days absent — please call the parent. See Follow-ups tab.`, { duration: 6000 });
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const openLeave = (s) => { setLeaveFor(s); setLFrom(date); setLTo(date); setLReason(""); };
  const saveLeave = async () => {
    if (lTo < lFrom) { toast.error("End date cannot be before start date"); return; }
    if (lReason.trim().length < 3) { toast.error("Please give a clear reason"); return; }
    setLSaving(true);
    try {
      await api.post("/teacher/student-attendance/advance-leave", {
        student_id: leaveFor.student_id, date_from: lFrom, date_to: lTo, reason: lReason.trim() });
      toast.success("Advance leave recorded");
      setLeaveFor(null);
      loadRoster();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not record leave"); }
    finally { setLSaving(false); }
  };

  const live = {
    total: roster.length,
    present: roster.filter((s) => s.status !== "absent").length,
    informed: roster.filter((s) => s.status === "absent" && s.absence_type === "informed").length,
    uninformed: roster.filter((s) => s.status === "absent" && s.absence_type === "uninformed").length,
  };

  return (
    <div className="space-y-5 w-full max-w-full overflow-x-hidden" data-testid="student-attendance">
      <PendingAttendance refreshKey={pendKey} onPick={(d, k) => { setDate(d); setKlass(k); }} />
      <div className="rounded-2xl bg-white border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">Date (India)</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="sa-date" /></div>
        <div><Label className="text-xs">Class / Batch</Label>
          <Select value={klass} onValueChange={setKlass}>
            <SelectTrigger data-testid="sa-class"><SelectValue placeholder="Select your class" /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c} value={c} data-testid={`sa-class-${c}`}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {savedAt && <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="sa-saved-note">Saved attendance loaded. You can edit and save again.</div>}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>
        : !klass ? <p className="text-sm text-slate-500">Select a class to load the roster.</p>
        : roster.length === 0 ? <p className="text-sm text-slate-500" data-testid="sa-empty">No students assigned to you in this class.</p>
        : (
        <>
          <div className="space-y-2" data-testid="sa-roster">
            {roster.map((s) => {
              const absent = s.status === "absent";
              return (
                <div key={s.student_id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`sa-row-${s.student_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{s.name}</p>
                      <p className="text-xs text-slate-500">Roll {s.roll_number}{s.advance_leave && <span className="ml-1 text-amber-600 font-medium">· Advance leave</span>}{!s.parent_email && <span className="ml-1 text-red-500">· No parent email</span>}</p>
                      {s.advance_leave && s.advance_leave_reason && <p className="text-[11px] text-amber-700 mt-0.5 truncate" data-testid={`sa-leave-reason-${s.student_id}`}>Leave: {s.advance_leave_reason}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!s.advance_leave && <Button size="sm" variant="ghost" className="text-blue-900 px-2" onClick={() => openLeave(s)} data-testid={`sa-leave-btn-${s.student_id}`}><CalendarPlus className="h-4 w-4" /></Button>}
                      <Badge className={absent ? "bg-red-600" : "bg-green-600"}>{absent ? "Absent" : "Present"}</Badge>
                      <Button size="sm" variant={absent ? "default" : "outline"} className={absent ? "bg-red-600 hover:bg-red-700 w-9" : "w-9"} onClick={() => toggleAbsent(s)} data-testid={`sa-a-${s.student_id}`}>A</Button>
                    </div>
                  </div>
                  {absent && (
                    <div className="mt-2 flex gap-2" data-testid={`sa-type-${s.student_id}`}>
                      {["informed", "uninformed"].map((t) => (
                        <Button key={t} size="sm" variant={s.absence_type === t ? "default" : "outline"} className={s.absence_type === t ? "bg-blue-900" : ""} onClick={() => setStudent(s.student_id, { absence_type: t })} data-testid={`sa-type-${t}-${s.student_id}`}>{t === "informed" ? "Informed" : "Uninformed"}</Button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-2xl bg-slate-900 text-white p-4 grid grid-cols-4 gap-2 text-center" data-testid="sa-totals">
            <div><p className="text-[11px] text-slate-300">Total</p><p className="font-extrabold">{(totals || live).total}</p></div>
            <div><p className="text-[11px] text-slate-300">Present</p><p className="font-extrabold text-green-400">{(totals || live).present}</p></div>
            <div><p className="text-[11px] text-slate-300">Informed</p><p className="font-extrabold text-amber-300">{(totals || live).informed_absent ?? live.informed}</p></div>
            <div><p className="text-[11px] text-slate-300">Uninformed</p><p className="font-extrabold text-red-400">{(totals || live).uninformed_absent ?? live.uninformed}</p></div>
          </div>

          <Button onClick={save} disabled={saving} className="w-full h-12 bg-green-600 hover:bg-green-700" data-testid="sa-save">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-5 w-5 mr-1.5" /> Save Attendance</>}
          </Button>
        </>
      )}

      <Dialog open={!!leaveFor} onOpenChange={(o) => !o && setLeaveFor(null)}>
        <DialogContent data-testid="sa-leave-dialog">
          <DialogHeader><DialogTitle>Advance leave — {leaveFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">From</Label><Input type="date" value={lFrom} onChange={(e) => setLFrom(e.target.value)} data-testid="sa-leave-from" /></div>
              <div><Label className="text-xs">To</Label><Input type="date" value={lTo} min={lFrom} onChange={(e) => setLTo(e.target.value)} data-testid="sa-leave-to" /></div>
            </div>
            <div><Label className="text-xs">Reason</Label><Textarea rows={2} value={lReason} onChange={(e) => setLReason(e.target.value)} placeholder="e.g. Informed by parent — family function" data-testid="sa-leave-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveFor(null)} data-testid="sa-leave-cancel">Cancel</Button>
            <Button onClick={saveLeave} disabled={lSaving} className="bg-blue-900 hover:bg-blue-950" data-testid="sa-leave-save">
              {lSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record leave"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TeacherHolidays classes={classes} onChanged={() => setPendKey((k) => k + 1)} />
    </div>
  );
}
